import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,          // NEW
  DialogActions,   // NEW
  DialogContent,   // NEW
  DialogTitle,     // NEW
  Grid,
  IconButton,
  Typography
} from "@mui/material";
import { useSnackbar } from "notistack";
import { useCreateVoucherImageMutation, usePaymentInitTransactionMutation } from "../../../store/api";
import { AttachFile } from "@mui/icons-material";
import { useSelector } from "react-redux";
import { RootState } from "../../../store/types/rootState";
import DeleteIcon from "@mui/icons-material/Delete";
import { useNavigate } from "react-router-dom";
import { getPrimaryColorByAccountId, getThemeByAccountId } from "../../../utils/styleConstants";
import { isTalonario } from "../../../utils/raffleType";
import { BankAccountItem } from "../../../constants/bankAccounts";
import { IPaymentInitTransactionResponse } from "../../../store/interfaces/IPaymentInitTransactionResponse";

interface TransferPaymentMethodProps {
  purchaserData: any;
  selectedPaymentMethod: string;
  raffleId: string;
  ticketQuantity: number;
  selectedTickets: number[] | undefined;
  totalAmount: number;
  totalUSDAmount: number;
  minPurchase: number;
  setOpen: (open: boolean) => void;
  selectedBankAccount?: BankAccountItem | undefined;
}

export const TransferPaymentMethod: React.FC<TransferPaymentMethodProps> = ({
  purchaserData,
  selectedPaymentMethod,
  raffleId,
  ticketQuantity,
  selectedTickets,
  totalAmount,
  totalUSDAmount,
  minPurchase,
  selectedBankAccount,
}: TransferPaymentMethodProps) => {
  const { enqueueSnackbar } = useSnackbar();
  const accountId = useSelector(({ account }: RootState) => account.id);
  const accountInformation = useSelector(({ account }: RootState) => account);
  const raffleType = useSelector(({ raffle }: RootState) => raffle.type);
  const hasReferenceNumber = useSelector(({ raffle }: RootState) => raffle.hasReferenceNumber);
  const isPremiumRaffle = useSelector(({ raffle }: RootState) => raffle.isPremium);

  const [voucherImages, setVoucherImages] = useState<File[]>([]);
  // En talonario el comprobante es opcional al apartar: se puede adjuntar después desde el link de tickets
  const voucherRequired = accountId !== 'super-rifas-latam' && !isTalonario(raffleType);

  const getSubmitLabel = () => {
    // Talonario: sin comprobante solo se apartan los números; con comprobante se compran
    if (isTalonario(raffleType)) return voucherImages.length > 0 ? 'Comprar números' : 'Apartar números';
    if (raffleType === 'raffle' || raffleType === 'lottery') return 'Comprar tickets';
    return 'Participar ahora';
  };
  const [showWhatsappDialog, setShowWhatsappDialog] = useState(false); // NEW

  const [initTransaction, { isLoading: isLoadingInitTransaction, data: dataInitTransaction }] =
    usePaymentInitTransactionMutation();
  const [createVoucher, { isLoading: isLoadingCreateVoucher, data: voucherSaved }] =
    useCreateVoucherImageMutation();

  const navigate = useNavigate();

  const handleImageChange = (files: FileList | null) => {
    if (!files) return;
    const voucher = Array.from(files).slice(0, 1);
    setVoucherImages(voucher);
  };

  const removeVoucherImage = () => {
    setVoucherImages([]);
  };

  const redirectToWhatsapp = (paymentInitResponse: IPaymentInitTransactionResponse) => {
    const purchaserEmail = paymentInitResponse.purchaser.email;
    const purchaserName = paymentInitResponse.purchaser.name;

    let whatsappNumber = accountInformation.socialMediaList.find(sm => sm.name === 'supportWhatsapp')?.username;
    if (accountId === 'erick-gomez') {
      whatsappNumber = '+56923950305';
    }

    const message = `Hola, soy ${purchaserName}. Compré ${ticketQuantity} números en la rifa de ${accountInformation.name}. En el siguiente link puedo revisar la información y el estado de mi compra: ${accountInformation.url}/self-verification/${purchaserEmail}?raffleId=${raffleId}`;
    const whatsappUrl = `https://wa.me/${whatsappNumber}/?text=${encodeURIComponent(message)}`;

    window.location.href = whatsappUrl;
  };

  // REMOVED: contador y redirección automática con setInterval
  // useEffect(() => { ... }, [dataInitTransaction, voucherSaved]);

  // NEW: solo abrir el modal cuando corresponda (sin redirigir automáticamente)
  useEffect(() => {
    if (voucherSaved === true && isPremiumRaffle && dataInitTransaction) {
      setShowWhatsappDialog(true);
    }
  }, [voucherSaved, isPremiumRaffle, dataInitTransaction]);

  // NEW: mientras se crea la orden o se guarda el comprobante, advertir antes
  // de cerrar/recargar la pestaña para que el proceso no quede a medias.
  const isProcessing = isLoadingInitTransaction || isLoadingCreateVoucher;
  useEffect(() => {
    if (!isProcessing) return;
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [isProcessing]);

  const handleInitTransaction = async () => {
    try {
      if (!purchaserData.name || !purchaserData.email || !purchaserData.phone || (hasReferenceNumber && !isTalonario(raffleType) && !purchaserData.referenceNumber)) {
        enqueueSnackbar("Ingrese todos los datos de contacto.", {
          variant: "error",
          anchorOrigin: { horizontal: "right", vertical: "top" },
        });
        return;
      }

      if (!selectedPaymentMethod) return;

      const response = await initTransaction({
        raffleId,
        conf: {
          referenceNumber: purchaserData.referenceNumber,
          ticketQuantity,
          paymentMethodId: selectedPaymentMethod,
          purchaser: purchaserData,
          selectedTickets,
          targetBankAccount: {
            id: selectedBankAccount?.bankId || 'Banco no identificado',
            name: selectedBankAccount?.name || 'Nombre no identificado',
            accountNumber: selectedBankAccount?.accountNumber || 'Cuenta no identidiaca'
          }
        },
      }).unwrap();

      if (response.status === "TRANSFER_PAYMENT_CREATED" && selectedPaymentMethod === "transfer") {
        if (isTalonario(raffleType)) {
          if (voucherImages.length > 0) {
            try {
              await createVoucher({
                accountId: accountId,
                imageFile: voucherImages[0],
                imageName: response.purchaseOrderId,
                raffleId: raffleId,
              }).unwrap();
            } catch {
              enqueueSnackbar("No pudimos subir tu comprobante. Puedes adjuntarlo desde tu link de tickets.", {
                variant: "warning",
                anchorOrigin: { horizontal: "right", vertical: "top" },
              });
            }
          }
          enqueueSnackbar("¡Tus números quedaron apartados!", {
            variant: "success",
            anchorOrigin: { horizontal: "right", vertical: "top" },
          });
          navigate(`/self-verification/${purchaserData.email}?raffleId=${raffleId}`);
        } else if (accountId === "super-rifas-latam") {
          navigate(`/successPurchase?raffleId=${raffleId}`);
        } else {
          createVoucher({
            accountId: accountId,
            imageFile: voucherImages[0],
            imageName: response.purchaseOrderId,
            raffleId: raffleId,
          })
            .then(() => {
              enqueueSnackbar(response.description, {
                variant: "success",
                anchorOrigin: { horizontal: "right", vertical: "top" },
              });
              if (!isPremiumRaffle) {
                navigate(`/successPurchase?raffleId=${raffleId}`);
              }
              // Si es premium, el useEffect abrirá el modal (showWhatsappDialog)
            })
            .catch((error: any) => {
              enqueueSnackbar(error, {
                variant: "error",
                anchorOrigin: { horizontal: "right", vertical: "top" },
              });
            });
        }
        return;
      }

      if (response.status === "LIMIT_EXCEEDED" || response.status === "RAFFLE_NO_LONGER_AVAILABLE") {
        enqueueSnackbar(response.error, {
          variant: "error",
          anchorOrigin: { horizontal: "right", vertical: "top" },
        });
      } else {
        console.log("response.status =>", response.status);
        enqueueSnackbar("Error al momento de ir al pago!", {
          variant: "error",
          anchorOrigin: { horizontal: "right", vertical: "top" },
        });
      }
    } catch (error) {
      console.log("response.error =>", error);
      enqueueSnackbar("Error al momento de ir al pago!", {
        variant: "error",
        anchorOrigin: { horizontal: "right", vertical: "top" },
      });
    }
  };

  return (
    <Grid container>
      {(!dataInitTransaction || dataInitTransaction.status === "UNAUTHORIZED_FOR_GENERATE_TICKETS") && !isLoadingInitTransaction && (
        <Grid item sx={{ width: "100%" }}>
          {(voucherRequired || isTalonario(raffleType)) && (
            <Button
              variant="text"
              component="label"
              startIcon={<AttachFile />}
              sx={{ border: "inherit", color: `${getThemeByAccountId(accountId) === 'dark' ? '#FFF !important' : '#000 !important'}` }}
            >
              {isTalonario(raffleType)
                ? 'Adjuntar comprobante (opcional)'
                : raffleType === 'raffle' || raffleType === 'lottery'
                ? `Click AQUÍ para subir comprobante de pago ${selectedBankAccount && `${selectedBankAccount.bank} - ${selectedBankAccount && selectedBankAccount?.ticketPriceAmount * ticketQuantity}$`}`
                : 'Subir screenshot de tu perfil de instagram'}
              <input
                type="file"
                accept="image/*"
                multiple
                hidden
                onChange={(e) => handleImageChange(e.target.files)}
                key={`voucher-input-button`}
              />
            </Button>
          )}

          {voucherImages.map((image, index) => (
            <Box key={`image-preview-${index}`}>
              <img src={URL.createObjectURL(image)} width={"100%"} />
              <Box display={"flex"} justifyContent={"center"}>
                <IconButton onClick={() => removeVoucherImage()}>
                  <DeleteIcon />
                </IconButton>
              </Box>
            </Box>
          ))}

          <Button
            disabled={
              (voucherImages.length === 0 && voucherRequired) ||
              !selectedPaymentMethod ||
              isLoadingInitTransaction ||
              totalUSDAmount === 0 ||
              ticketQuantity === 0 ||
              ticketQuantity < minPurchase
            }
            fullWidth
            type="submit"
            color="primary"
            sx={{
              opacity:
                (voucherImages.length === 0 && voucherRequired) ||
                totalUSDAmount === 0 ||
                ticketQuantity === 0 ||
                ticketQuantity < minPurchase
                  ? 0.3
                  : 1,
              backgroundColor: `${getPrimaryColorByAccountId(accountId)} !important`,
              borderColor: "#000",
              mt: 2,
            }}
            variant="contained"
            onClick={handleInitTransaction}
          >
            {!isLoadingInitTransaction ? (
              <Typography color={`${getThemeByAccountId(accountId) === 'dark' ? '#000 !important' : '#000 !important'}`} ml={1} fontWeight={"bold"}>
                {getSubmitLabel()}
              </Typography>
            ) : (
              <CircularProgress size="2rem" />
            )}
          </Button>
        </Grid>
      )}

      {/* NEW: Modal grande que bloquea la pantalla mientras se procesa la compra.
          No se puede cerrar (sin onClose, sin escape, sin click fuera) para que el
          usuario no abandone la plataforma antes de que termine de guardarse el comprobante. */}
      <Dialog
        open={isProcessing}
        fullScreen
        disableEscapeKeyDown
        aria-labelledby="processing-dialog-title"
        PaperProps={{
          sx: {
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            px: 3,
            py: 6,
            color: "#FFF",
            background: "radial-gradient(circle at 50% 35%, #1a1a1a 0%, #000 70%)",
          },
        }}
      >
        <Box position="relative" display="flex" alignItems="center" justifyContent="center" mb={4}>
          <CircularProgress
            size={84}
            thickness={3}
            variant="indeterminate"
            sx={{ color: getPrimaryColorByAccountId(accountId) }}
          />
          <CircularProgress
            size={84}
            thickness={3}
            variant="determinate"
            value={100}
            sx={{ color: "rgba(255,255,255,0.12)", position: "absolute" }}
          />
        </Box>

        <Typography
          id="processing-dialog-title"
          variant="h4"
          textAlign="center"
          fontWeight={800}
          gutterBottom
          sx={{ letterSpacing: 0.5, color: "#FFF" }}
        >
          {isLoadingInitTransaction ? "Creando tu orden de compra" : "Guardando tu comprobante"}
        </Typography>

        <Typography
          variant="body1"
          textAlign="center"
          sx={{ maxWidth: 440, color: "rgba(255,255,255,0.75)", lineHeight: 1.6 }}
        >
          Estamos procesando tu compra. Por favor{" "}
          <Box component="strong" sx={{ color: "#FFF" }}>
            no cierres ni recargues esta pestaña
          </Box>{" "}
          hasta que el proceso termine; de lo contrario tu comprobante podría no quedar registrado.
        </Typography>

        <Box
          mt={4}
          px={2.5}
          py={1.5}
          sx={{
            maxWidth: 440,
            width: "100%",
            textAlign: "center",
            borderRadius: 2,
            border: "1px solid rgba(255,255,255,0.15)",
            backgroundColor: "rgba(255,255,255,0.04)",
          }}
        >
          <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.65)" }}>
            Esto puede tardar unos segundos. Gracias por tu paciencia 🙌
          </Typography>
        </Box>
      </Dialog>

      {/* NEW: Modal que exige clic del usuario para ir a WhatsApp */}
      <Dialog
        open={showWhatsappDialog}
        onClose={() => { /* bloqueamos cierre para forzar acción explícita */ }}
        disableEscapeKeyDown
        aria-labelledby="whatsapp-dialog-title"
      >
        <DialogTitle id="whatsapp-dialog-title">Confirmar envío por WhatsApp</DialogTitle>
        <DialogContent sx={{ textAlign: 'center', display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
          <Typography>
            Tu comprobante fue recibido. Para finalizar, debes enviar los datos de tu compra por WhatsApp haciendo clic en el botón.
          </Typography>
          <Box mt={2}>
            <Alert severity="info">
              Desde este link podras hacer seguimiento de tu compra y de la confirmación de tu pago.
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            onClick={() => {
              if (dataInitTransaction) {
                redirectToWhatsapp(dataInitTransaction);
              }
            }}
          >
            Ir a WhatsApp
          </Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
};
